import * as React from "react";
import { cn } from "../lib/cn.js";
import { EDUVIAOS_MARK_PATHS } from "./eduviaos-logo.js";

/**
 * EduViaOS brand loader — the canonical loading indicator for every
 * portal. The two pages of the book mark draw themselves in, the guiding
 * star rises from the spine, then the lockup fades and the loop restarts
 * (1.4s).
 *
 * Motion notes (emil-design-eng):
 *   - Keyframes live in `@eduviaos/design-tokens/tokens.css`
 *     (`eduviaos-loader-*`), which every app imports.
 *   - Strokes animate `stroke-dashoffset` on a 64-unit SVG (paint only,
 *     no layout); the star animates `transform` + `opacity` only, rotating
 *     about its own centre (`transform-box: fill-box`).
 *   - Each segment eases out; nothing starts from `scale(0)`.
 *   - `prefers-reduced-motion: reduce` swaps the loop for a static mark
 *     with a slow opacity breath.
 */
export interface BrandLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width/height of the mark in px. Default 48. */
  size?: number;
  /** Screen-reader announcement. Default "Loading". */
  label?: string;
}

export function BrandLoader({
  size = 48,
  label = "Loading",
  className,
  ...props
}: BrandLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("inline-grid place-items-center", className)}
      style={{ width: size, height: size }}
      {...props}
    >
      <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" className="eduviaos-loader size-full">
        <path
          d={EDUVIAOS_MARK_PATHS.ink}
          pathLength={1}
          className="eduviaos-loader__page"
          stroke="var(--eduviaos-ink, #16213a)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d={EDUVIAOS_MARK_PATHS.teal}
          pathLength={1}
          className="eduviaos-loader__page eduviaos-loader__page--second"
          stroke="var(--eduviaos-teal, #1b7f7c)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d={EDUVIAOS_MARK_PATHS.star}
          className="eduviaos-loader__star"
          fill="var(--eduviaos-saffron, #e8892b)"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Full-surface loading state built around {@link BrandLoader}. Use it for
 * route-, session- and Suspense-level waits instead of bare "Loading…"
 * text.
 */
export interface LoadingScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional caption below the mark (e.g. "Restoring session…"). */
  label?: string;
  /** Mark size in px. Default 56. */
  size?: number;
  /**
   * `true` (default) pins the loader over the whole viewport
   * (`fixed inset-0`) so it is centred regardless of the parent's height.
   * `false` fills and centres within the parent region instead.
   */
  fullscreen?: boolean;
}

export function LoadingScreen({
  label,
  size = 56,
  fullscreen = true,
  className,
  ...props
}: LoadingScreenProps) {
  return (
    <div
      className={cn(
        "grid place-items-center px-4",
        fullscreen ? "bg-background fixed inset-0 z-50" : "h-full min-h-40 w-full",
        className,
      )}
      data-testid="loading-screen"
      {...props}
    >
      <div className="flex flex-col items-center gap-4">
        <BrandLoader size={size} label={label ?? "Loading"} />
        {label ? (
          <p
            aria-hidden="true"
            className="text-muted-foreground animate-pulse text-sm motion-reduce:animate-none"
          >
            {label}
          </p>
        ) : null}
      </div>
    </div>
  );
}
